import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Search, Gauge, Wrench, LineChart, Megaphone, ClipboardCheck, UtensilsCrossed, Target, GraduationCap, LayoutDashboard, Globe2, Award, FileText, BarChart3, MessagesSquare, Crown, CheckCircle2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/primitives";
import { CTABand } from "../components/CTABand";
import { IMAGES, API } from "../lib/site";
import { PLAN_INFO, formatPrice } from "../lib/plans";

const STEPS = [
    {
        icon: Search,
        num: "Step 1",
        title: "Understand",
        desc: "Assess current sustainability practices.",
    },
    {
        icon: Gauge,
        num: "Step 2",
        title: "Measure",
        desc: "Identify important environmental and operational indicators.",
    },
    {
        icon: Wrench,
        num: "Step 3",
        title: "Improve",
        desc: "Develop practical improvement opportunities.",
    },
    {
        icon: LineChart,
        num: "Step 4",
        title: "Track",
        desc: "Monitor progress and results.",
    },
    {
        icon: Megaphone,
        num: "Step 5",
        title: "Communicate",
        desc: "Share credible sustainability progress with employees, guests and stakeholders.",
    },
];

const PROCESS = [
    "Choose Plan",
    "Create Hotel Account",
    "Enter Hotel Information",
    "Make Payment",
    "Complete Assessment",
    "Access Dashboard",
    "Start Improving",
];

const SERVICES = [
    {
        icon: ClipboardCheck,
        title: "Sustainability Assessment",
        desc: "An online assessment covering food and food waste, energy, water, waste management, sustainable purchasing, employees, community involvement and sustainability management — with a score, strengths, gaps and recommended actions.",
    },
    {
        icon: UtensilsCrossed,
        title: "Food-Waste Management",
        desc: "Tools and guidance to measure and reduce food waste, improve purchasing and inventory practices, cut unnecessary food costs and track progress.",
    },
    {
        icon: Target,
        title: "Sustainability Improvement Plan",
        desc: "Practical recommendations and an action plan built from your hotel's own assessment results.",
    },
    {
        icon: GraduationCap,
        title: "GGH Learning Hub",
        desc: "A members-only learning area for hotel teams — short, practical modules on sustainable hospitality, food waste, water and energy awareness, housekeeping and team engagement.",
    },
    {
        icon: LayoutDashboard,
        title: "Sustainability Dashboard",
        desc: "A simple, visual private dashboard: your score, assessment results, recommended actions and progress over time.",
    },
    {
        icon: Globe2,
        title: "GGH Hotel Profile",
        desc: "A public sustainability profile on the GGH website showing your initiatives, progress, location and participation status.",
    },
    {
        icon: Award,
        title: "GGH Recognition",
        desc: "Eligible hotels can receive GGH sustainability recognition after completing the assessment and meeting the applicable requirements. GGH recognition is not government certification, accreditation or regulatory approval — and is never awarded simply for paying membership.",
    },
    {
        icon: FileText,
        title: "Sustainability Reporting",
        desc: "Generate or request simple progress reports summarizing your performance, improvements and measurable results.",
    },
    {
        icon: BarChart3,
        title: "Benchmarking",
        desc: "Higher-level plans can compare performance with anonymized GGH benchmarks as sufficient verified data becomes available.",
    },
    {
        icon: MessagesSquare,
        title: "Consulting & Support",
        desc: "Request additional sustainability consulting, implementation support, assessments and training from GGH.",
    },
];

const ForHotels = () => {
    const [prices, setPrices] = useState({});
    useEffect(() => {
        axios.get(`${API}/plans`).then((res) => setPrices(res.data.prices || {})).catch(() => {});
    }, []);
    return (
    <main>
        <SEO
            title="For Hotels | Global Green Hotel"
            description="Make sustainability work for your hotel. GGH helps hotels understand where sustainability can create environmental and business value."
        />
        <PageHero
            testid="for-hotels-hero"
            eyebrow="For Hotels"
            titleLines={["Make Sustainability Work", "for Your Hotel"]}
            description="GGH aims to help hotels understand where sustainability can create environmental and business value — for owners, general managers, sustainability managers and hospitality executives."
        />

        {/* Pathway */}
        <section data-testid="hotel-pathway" className="py-24 sm:py-32">
            <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <Eyebrow>The GGH Hotel Pathway</Eyebrow>
                    <h2 className="font-display mt-4 max-w-3xl text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                        Five steps from intention to credible progress
                    </h2>
                </Reveal>

                <ol className="mt-16 grid gap-4 md:grid-cols-5" data-testid="pathway-steps">
                    {STEPS.map(({ icon: Icon, num, title, desc }, i) => (
                        <Reveal key={title} delay={0.09 * i} y={26}>
                            <li
                                data-testid={`pathway-step-${i + 1}`}
                                className="card-lift group relative flex h-full flex-col rounded-2xl border border-[#E8E6E1] bg-white p-6 transition-colors duration-300 hover:border-[#1A3B2B]"
                            >
                                <span className="eyebrow-green">{num}</span>
                                <span className="mt-5 flex h-12 w-12 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A] transition-colors duration-300 group-hover:bg-[#274E3A]">
                                    <Icon size={20} aria-hidden="true" />
                                </span>
                                <h3 className="font-display mt-6 text-2xl font-medium tracking-tight text-[#0A1711]">
                                    {title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-[#4A5D54]">{desc}</p>
                                {i < STEPS.length - 1 && (
                                    <span
                                        aria-hidden="true"
                                        className="absolute -right-3 top-1/2 hidden h-px w-6 bg-[#C2A160] md:block"
                                    />
                                )}
                            </li>
                        </Reveal>
                    ))}
                </ol>

                <Reveal delay={0.4}>
                    <div className="mt-14 flex flex-wrap gap-4">
                        <Link to="/sustainability-check" data-testid="cta-free-check" className="btn-gold">
                            Take the Free Check
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </Link>
                        <Link to="/contact" data-testid="cta-talk-to-ggh" className="btn-green">
                            Talk to GGH
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </Link>
                        <Link to="/get-involved" data-testid="cta-join-pilot" className="btn-outline-green">
                            Join a Pilot
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>

        {/* Image + value band */}
        <section data-testid="hotel-value" className="grain relative overflow-hidden bg-[#07211A] py-24 sm:py-32">
            <div className="relative z-[2] mx-auto grid max-w-[1440px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-12">
                <Reveal className="relative">
                    <div className="overflow-hidden rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
                        <img
                            src={IMAGES.interior}
                            alt="A calm, plant-filled hotel interior designed with sustainability in mind"
                            className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                            loading="lazy"
                        />
                        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-[#04150D]/40 to-transparent" />
                    </div>
                    <div aria-hidden="true" className="absolute -left-5 -top-5 -z-[1] h-full w-full rounded-2xl border border-[#C2A160]/30" />
                </Reveal>
                <div>
                    <Reveal>
                        <Eyebrow dark>Why It Matters</Eyebrow>
                        <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#FDFBF7] sm:text-4xl">
                            Sustainability that pays its own way
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-7 text-base leading-relaxed text-[#FDFBF7]/70 md:text-lg">
                            Hotels face pressure to remain financially competitive while meeting
                            rising expectations from guests, employees and communities. GGH
                            believes the answer is not sustainability as an extra cost — but
                            sustainability as better operations.
                        </p>
                        <p className="font-display mt-6 text-xl italic leading-relaxed text-[#D9B96A]">
                            Measure what matters. Improve what you can. Communicate what is real.
                        </p>
                    </Reveal>
                </div>
            </div>
        </section>

        {/* GGH Services */}
        <section data-testid="services-section" className="py-24 sm:py-32">
            <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <Eyebrow>GGH Services</Eyebrow>
                    <h2 className="font-display mt-4 max-w-3xl text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                        Sustainability That Makes Business Sense
                    </h2>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#4A5D54] md:text-lg">
                        Global Green Hotel helps hotels improve sustainability through practical
                        assessment, measurement, training, improvement planning, and recognition —
                        reducing environmental impact, improving operational efficiency, cutting
                        unnecessary costs, and communicating progress responsibly.
                    </p>
                </Reveal>
                <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
                    {SERVICES.map(({ icon: Icon, title, desc }, i) => (
                        <Reveal key={title} delay={0.05 * i} y={22} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
                            <article className="card-lift flex h-full flex-col rounded-2xl border border-[#E8E6E1] bg-white p-7 transition-colors duration-300 hover:border-[#1A3B2B]">
                                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A]">
                                    <Icon size={20} aria-hidden="true" />
                                </span>
                                <h3 className="font-display mt-6 text-xl font-medium tracking-tight text-[#0A1711]">{title}</h3>
                                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-[#4A5D54]">{desc}</p>
                            </article>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.2}>
                    <p className="mt-8 max-w-3xl text-xs leading-relaxed text-[#4A5D54]">
                        GGH is a developing hospitality sustainability initiative. GGH membership,
                        GGH Assessment, GGH Sustainability Score, GGH Recognition, the GGH
                        Sustainability Program and GGH Hotel Profiles are not government
                        certification, accreditation, or regulatory approval, and recognition is
                        never awarded simply for purchasing a membership.
                    </p>
                </Reveal>
            </div>
        </section>

        {/* Membership plans */}
        <section id="membership" data-testid="membership-section" className="grain relative scroll-mt-24 overflow-hidden bg-[#04150D] py-24 sm:py-32">
            <div className="relative z-[2] mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <Eyebrow dark>Membership Plans</Eyebrow>
                    <h2 className="font-display mt-4 max-w-3xl text-3xl font-medium tracking-tight text-[#FDFBF7] sm:text-4xl">
                        Measure. Improve. Save. Demonstrate Your Impact.
                    </h2>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#FDFBF7]/70 md:text-lg">
                        Simple annual membership for independent hotels, resorts and international
                        groups. Choose the level that matches your ambition.
                    </p>
                </Reveal>

                <div className="mt-14 grid gap-5 lg:grid-cols-3" data-testid="plans-grid">
                    {PLAN_INFO.map((p, i) => (
                        <Reveal key={p.key} delay={0.1 * i} y={26}>
                            <article
                                data-testid={`plan-${p.key}`}
                                className={`relative flex h-full flex-col rounded-2xl border p-8 ${
                                    p.popular
                                        ? "border-[#C2A160] bg-[#07211A] shadow-[0_24px_70px_rgba(194,161,96,0.15)]"
                                        : "border-white/10 bg-[#07211A]/60"
                                }`}
                            >
                                {p.popular && (
                                    <span className="absolute -top-3 left-8 inline-flex items-center gap-1 rounded-full bg-[#C2A160] px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[#0A1711]">
                                        <Crown size={11} aria-hidden="true" /> Most Popular
                                    </span>
                                )}
                                <h3 className="font-display text-2xl font-medium text-[#FDFBF7]">{p.name}</h3>
                                <p className="mt-2 text-sm text-[#FDFBF7]/60">{p.tagline}</p>
                                <ul className="mt-7 flex-1 space-y-3">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2.5 text-sm text-[#FDFBF7]/80">
                                            <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#C2A160]" aria-hidden="true" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to={`/join?plan=${p.key}`}
                                    data-testid={`plan-join-${p.key}`}
                                    className={`${p.popular ? "btn-gold" : "btn-outline-light"} mt-8 w-full`}
                                >
                                    Join GGH
                                    <ArrowUpRight size={16} aria-hidden="true" />
                                </Link>
                            </article>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.3}>
                    <p className="mt-10 max-w-3xl text-sm leading-relaxed text-[#FDFBF7]/60">
                        Large hotel group, resort collection or organization with customized needs?{" "}
                        <Link to="/contact" data-testid="plans-contact" className="font-bold text-[#D9B96A] underline-offset-4 hover:underline">
                            Contact GGH
                        </Link>{" "}
                        for tailored services.
                    </p>
                </Reveal>

                {/* Subscription process */}
                <Reveal delay={0.35}>
                    <ol className="mt-14 flex flex-wrap items-center gap-2" data-testid="subscription-process" aria-label="Subscription process">
                        {PROCESS.map((step, i) => (
                            <li key={step} className="flex items-center gap-2">
                                <span className="rounded-full border border-[#C2A160]/40 bg-[#C2A160]/10 px-4 py-1.5 text-xs font-bold text-[#D9B96A]">
                                    {step}
                                </span>
                                {i < PROCESS.length - 1 && (
                                    <span aria-hidden="true" className="text-[#C2A160]/60">→</span>
                                )}
                            </li>
                        ))}
                    </ol>
                </Reveal>

                <Reveal delay={0.4}>
                    <div className="mt-12 flex flex-wrap gap-4">
                        <Link to="/join" data-testid="cta-join-ggh" className="btn-gold">
                            Join GGH
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </Link>
                        <a href="#membership" data-testid="cta-explore-membership" className="btn-outline-light">
                            Explore Membership
                        </a>
                        <Link to="/hotel/login" data-testid="cta-start-assessment" className="btn-outline-light">
                            Start Your Assessment
                        </Link>
                        <Link to="/hotel/login" data-testid="cta-hotel-login" className="btn-outline-light">
                            Hotel Login
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>

        <CTABand
            testid="cta-band-for-hotels"
            heading="Ready to Explore What Sustainability Could Do for Your Hotel?"
            text="Start a conversation with GGH — no commitments, just a practical discussion about where value could be created."
            primary={{ to: "/contact", label: "Talk to GGH" }}
            secondary={{ to: "/framework", label: "Explore the Framework" }}
        />
    </main>
    );
};

export default ForHotels;
